import React, { useMemo, useState, Children } from 'react';
import { IconSearch, IconX, IconFilter } from '@tabler/icons-react';
import { ConversationHistoryCard } from '../components/ConversationHistoryCard';
import { FilterModal } from '../components/FilterModal';
import { YeppyChatMode } from '../components/EmptyChatSuggestions';
// Children data (matching ActivitiesPage)
interface Child {
  id: string;
  name: string;
  avatar: string;
}
const familyChildren: Child[] = [{
  id: 'child-1',
  name: 'Sofia',
  avatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg'
}, {
  id: 'child-2',
  name: 'Marco',
  avatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg'
}];
// Mock data per lo storico
const mockHistory = [{
  id: '1',
  title: 'Iscrizione scuola elementare per Sofia',
  childName: 'Sofia',
  childAvatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg',
  date: '15 Gen 2024',
  category: 'Scuola',
  categoryColor: '#AEDAFF',
  taskCount: 8,
  chatMode: 'attivita' as YeppyChatMode
}, {
  id: '2',
  title: 'Rinnovo passaporto per Marco',
  childName: 'Marco',
  childAvatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg',
  date: '12 Gen 2024',
  category: 'Burocrazia',
  categoryColor: '#FAD88A',
  taskCount: 6,
  chatMode: 'informazioni' as YeppyChatMode
}, {
  id: '3',
  title: 'Cambio pediatra per Sofia',
  childName: 'Sofia',
  childAvatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg',
  date: '10 Gen 2024',
  category: 'Salute',
  categoryColor: '#B0EC9C',
  taskCount: 5,
  chatMode: 'attivita' as YeppyChatMode
}, {
  id: '4',
  title: 'Iscrizione corso di nuoto per Marco',
  childName: 'Marco',
  childAvatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg',
  date: '8 Gen 2024',
  category: 'Sport',
  categoryColor: '#DDD6FE',
  taskCount: 4,
  chatMode: 'attivita' as YeppyChatMode
}, {
  id: '5',
  title: 'Documenti per gita scolastica',
  childName: 'Sofia',
  childAvatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg',
  date: '5 Gen 2024',
  category: 'Scuola',
  categoryColor: '#AEDAFF',
  taskCount: 3,
  chatMode: 'informazioni' as YeppyChatMode
}, {
  id: '6',
  title: 'Visita oculistica per Marco',
  childName: 'Marco',
  childAvatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg',
  date: '3 Gen 2024',
  category: 'Salute',
  categoryColor: '#B0EC9C',
  taskCount: 4,
  chatMode: 'ascolto' as YeppyChatMode
}];
interface HistoryPageProps {
  onOpenConversation: (conversationId: string) => void;
}
export function HistoryPage({
  onOpenConversation
}: HistoryPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChildFilter, setSelectedChildFilter] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('Tutte');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const hasActiveFilters = selectedCategory !== 'Tutte' || selectedChildFilter !== null;
  const selectedChildName = selectedChildFilter ? familyChildren.find(c => c.id === selectedChildFilter)?.name || 'Tutti' : 'Tutti';
  // Filter conversations
  const filteredHistory = useMemo(() => {
    return mockHistory.filter(conversation => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = !query || conversation.title.toLowerCase().includes(query) || conversation.childName.toLowerCase().includes(query) || conversation.category.toLowerCase().includes(query);
      const matchesChild = !selectedChildFilter || conversation.childName === familyChildren.find(c => c.id === selectedChildFilter)?.name;
      const matchesCategory = selectedCategory === 'Tutte' || conversation.category === selectedCategory;
      return matchesSearch && matchesChild && matchesCategory;
    });
  }, [searchQuery, selectedChildFilter, selectedCategory]);
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  const clearFilters = () => {
    setSelectedCategory('Tutte');
    setSelectedChildFilter(null);
  };
  const handleChildChange = (child: string) => {
    if (child === 'Tutti') {
      setSelectedChildFilter(null);
    } else {
      const found = familyChildren.find(c => c.name === child);
      setSelectedChildFilter(found ? found.id : null);
    }
  };
  return <div className="flex flex-col h-full" data-id="element-795">
      {/* Filters header */}
      <div className="px-4 pb-3" data-id="element-796">
        {/* Children selector */}
        <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-id="element-797">
          {/* "Tutti" pill */}
          <button onClick={() => setSelectedChildFilter(null)} className={`flex items-center gap-2 px-4 py-2 rounded-full font-poppins text-sm font-medium transition-all flex-shrink-0 ${selectedChildFilter === null ? 'bg-yeppy-text text-white' : 'bg-white border border-yeppy-text border-opacity-15 text-yeppy-text'}`} data-id="element-798">
            Tutti
          </button>

          {/* Child pills */}
          {familyChildren.map(child => <button key={child.id} onClick={() => setSelectedChildFilter(selectedChildFilter === child.id ? null : child.id)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-poppins text-sm font-medium transition-all flex-shrink-0 ${selectedChildFilter === child.id ? 'bg-yeppy-text text-white' : 'bg-white border border-yeppy-text border-opacity-15 text-yeppy-text'}`} data-id="element-799">
              <img src={child.avatar} alt={child.name} className={`w-6 h-6 rounded-full object-cover flex-shrink-0 ${selectedChildFilter === child.id ? 'border-2 border-white' : 'border border-yeppy-text border-opacity-10'}`} data-id="element-800" />
              <span data-id="element-801">{child.name}</span>
            </button>)}
        </div>

        {/* Search bar with filter button */}
        <div className="flex gap-2" data-id="element-802">
          <div className="relative flex-1" data-id="element-803">
            <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yeppy-text text-opacity-40 pointer-events-none z-10" stroke={1.5} data-id="element-804" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca conversazioni..." className="w-full pl-12 pr-10 py-3 rounded-2xl font-poppins text-sm text-yeppy-text bg-white border border-yeppy-text border-opacity-15 focus:outline-none focus:border-yeppy-text focus:border-opacity-30 transition-colors placeholder-yeppy-text placeholder-opacity-40" data-id="element-805" />
            {searchQuery && <button onClick={handleClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-yeppy-text bg-opacity-10 flex items-center justify-center transition-colors" aria-label="Cancella ricerca" data-id="element-806">
                <IconX className="w-4 h-4 text-yeppy-text" stroke={2.5} data-id="element-807" />
              </button>}
          </div>

          {/* Filter button */}
          <button onClick={() => setIsFilterModalOpen(true)} className={`relative flex items-center justify-center w-12 h-12 rounded-2xl transition-colors flex-shrink-0 ${hasActiveFilters ? 'bg-yeppy-text text-white' : 'bg-white text-yeppy-text border border-yeppy-text border-opacity-15'}`} aria-label="Apri filtri" data-id="element-808">
            <IconFilter className="w-5 h-5" stroke={1.5} data-id="element-809" />
            {hasActiveFilters && <span className="absolute -top-1 -right-1 w-3 h-3 bg-yeppy-green rounded-full border-2 border-yeppy-bg" data-id="element-810" />}
          </button>
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4" data-id="element-811">
        {filteredHistory.length > 0 ? <div className="space-y-2.5" data-id="element-812">
            {filteredHistory.map(conversation => <ConversationHistoryCard key={conversation.id} title={conversation.title} childName={conversation.childName} childAvatar={conversation.childAvatar} date={conversation.date} category={conversation.category} categoryColor={conversation.categoryColor} taskCount={conversation.taskCount} onClick={() => onOpenConversation(conversation.id)} data-id="element-813" />)}
          </div> : <div className="flex flex-col items-center justify-center h-full text-center px-4" data-id="element-814">
            <IconSearch className="w-16 h-16 text-yeppy-text text-opacity-30 mb-4" stroke={1.5} data-id="element-815" />
            <h3 className="font-rubik font-semibold text-base text-yeppy-text mb-2" data-id="element-816">
              Nessun risultato
            </h3>
            <p className="font-poppins text-sm text-yeppy-text text-opacity-60 max-w-xs" data-id="element-817">
              Non ho trovato conversazioni che corrispondono ai filtri
              selezionati.
            </p>
          </div>}
      </div>

      {/* Filter Modal */}
      <FilterModal isOpen={isFilterModalOpen} onClose={() => setIsFilterModalOpen(false)} selectedCategory={selectedCategory} selectedChild={selectedChildName} onCategoryChange={setSelectedCategory} onChildChange={handleChildChange} onClearFilters={clearFilters} data-id="element-818" />
    </div>;
}